import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { WatchlistService } from "../services/watchlist-service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import type { WatchlistItem } from "@/schemas";
import {
  editWatchlistItemFormSchema,
  type EditWatchlistItemFormData,
} from "@/schemas/user.schema";

interface EditItemDialogProps {
  item: WatchlistItem | null;
  watchlistId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

export function EditItemDialog({
  item,
  watchlistId,
  isOpen,
  onClose,
  onUpdated,
}: EditItemDialogProps) {
  const form = useForm<EditWatchlistItemFormData>({
    resolver: zodResolver(editWatchlistItemFormSchema),
    defaultValues: {
      note: "",
      target_price: undefined,
    },
  });

  const { isSubmitting } = form.formState;

  // Reset form when dialog opens with new item
  useEffect(() => {
    if (isOpen && item) {
      form.reset({
        note: item.note || "",
        target_price: item.target_price ?? undefined,
      });
    }
  }, [isOpen, item, form]);

  const onSubmit = async (data: EditWatchlistItemFormData) => {
    if (!item || !watchlistId) return;

    try {
      const payload: { note?: string | null; target_price?: number | null } = {
        note: data.note || null,
        target_price: data.target_price ?? null,
      };

      await WatchlistService.updateItem(watchlistId, item.item_id, payload);
      onUpdated();
      toast.success("Item updated");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update item");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {item?.asset?.symbol || "Item"}</DialogTitle>
          <DialogDescription>
            Update the note and target price for this asset.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="py-4 space-y-4">
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Note</FormLabel>
                    <FormControl>
                      <Input placeholder="Add a note..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="target_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Price</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter target price..."
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(
                            value === "" ? undefined : parseFloat(value)
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
